<script lang="ts">
  /**
   * JournalOverlay — quest log, narrative memory, and story arcs.
   * Fetches data from the engine via Tauri: journal entries, story arcs,
   * and tiered narrative memory.
   */
  import { getJournal, getStoryArcs, getNarrativeMemory } from "$lib/api/invoke";
  import type { JournalEntryInfo, StoryArcInfo, NarrativeMemoryInfo } from "$lib/api/invoke";

  let entries: JournalEntryInfo[] = $state([]);
  let arcs: StoryArcInfo[] = $state([]);
  let memory: NarrativeMemoryInfo | null = $state(null);
  let loading = $state(true);
  let activeTab: "events" | "stories" | "facts" = $state("events");

  async function load(): Promise<void> {
    try {
      const [j, a, m] = await Promise.all([getJournal(), getStoryArcs(), getNarrativeMemory()]);
      entries = Array.isArray(j) ? j : [];
      arcs = Array.isArray(a) ? a : [];
      memory = m;
    } catch {
      entries = [];
      arcs = [];
      memory = null;
    }
    loading = false;
  }

  load();

  function typeIcon(type: string): string {
    switch (type) {
      case "quest":
        return "[!]";
      case "note":
        return "[*]";
      case "lore":
        return "[~]";
      default:
        return "[ ]";
    }
  }

  function tensionBar(tension: number): string {
    const filled = Math.round(tension * 10);
    return "[" + "#".repeat(filled) + ".".repeat(10 - filled) + "]";
  }

  function statusLabel(status: string): string {
    switch (status) {
      case "brewing":
        return "BREWING";
      case "active":
        return "ACTIVE";
      case "climax":
        return "CLIMAX!";
      case "resolved":
        return "RESOLVED";
      case "failed":
        return "FAILED";
      case "abandoned":
        return "ABANDONED";
      default:
        return status.toUpperCase();
    }
  }

  /** Visible arcs: everything except "unknown" visibility. */
  let visibleArcs = $derived(arcs.filter((a) => a.visibility !== "unknown"));
</script>

<div class="journal-overlay">
  <h2 class="journal-overlay__title">JOURNAL (J)</h2>

  <div class="journal-overlay__border">+{"=".repeat(44)}+</div>

  <div class="journal-overlay__tabs">
    <button
      class="journal-overlay__tab"
      class:active={activeTab === "events"}
      onclick={() => (activeTab = "events")}
    >
      RECENT EVENTS
    </button>
    <button
      class="journal-overlay__tab"
      class:active={activeTab === "stories"}
      onclick={() => (activeTab = "stories")}
    >
      STORIES ({visibleArcs.length})
    </button>
    <button
      class="journal-overlay__tab"
      class:active={activeTab === "facts"}
      onclick={() => (activeTab = "facts")}
    >
      KEY FACTS
    </button>
  </div>

  <div class="journal-overlay__content">
    {#if loading}
      <span class="journal-overlay__empty">Loading...</span>
    {:else if activeTab === "events"}
      <!-- Recent Events: immediate + recent memory + journal entries -->
      {#if memory && (memory.immediate_text || memory.recent_text)}
        {#if memory.immediate_text}
          <div class="journal-overlay__section-label">
            RECENT ({memory.immediate_count} beats)
          </div>
          {#each memory.immediate_text.trim().split("\n") as line}
            {#if line.trim()}
              <p class="journal-overlay__memory-line">{line}</p>
            {/if}
          {/each}
        {/if}
        {#if memory.recent_text}
          <div class="journal-overlay__section-label">
            EARLIER ({memory.recent_count} summaries)
          </div>
          {#each memory.recent_text.trim().split("\n") as line}
            {#if line.trim()}
              <p class="journal-overlay__memory-line journal-overlay__memory-line--dim">
                {line}
              </p>
            {/if}
          {/each}
        {/if}
      {:else if entries.length > 0}
        {#each entries as entry}
          <div class="journal-overlay__entry">
            <div class="journal-overlay__header">
              <span class="journal-overlay__icon">{typeIcon(entry.entry_type)}</span>
              <span class="journal-overlay__entry-title">{entry.title}</span>
              <span class="journal-overlay__type">{entry.entry_type.toUpperCase()}</span>
            </div>
            <p class="journal-overlay__body">{entry.body}</p>
          </div>
        {/each}
      {:else}
        <span class="journal-overlay__empty">No events recorded yet.</span>
      {/if}
    {:else if activeTab === "stories"}
      <!-- Running Stories: visible arcs -->
      {#if visibleArcs.length === 0}
        <span class="journal-overlay__empty">No story threads discovered yet.</span>
      {:else}
        {#each visibleArcs as arc}
          <div class="journal-overlay__arc">
            <div class="journal-overlay__arc-header">
              <span class="journal-overlay__arc-title">{arc.title}</span>
              <span
                class="journal-overlay__arc-status"
                class:climax={arc.status === "climax"}
                class:resolved={arc.status === "resolved"}
                class:failed={arc.status === "failed"}
              >
                {statusLabel(arc.status)}
              </span>
            </div>
            <p class="journal-overlay__arc-desc">{arc.description}</p>
            <div class="journal-overlay__arc-tension">
              <span class="journal-overlay__arc-tension-label">TENSION</span>
              <span class="journal-overlay__arc-tension-bar">{tensionBar(arc.tension)}</span>
              <span class="journal-overlay__arc-tension-pct">{Math.round(arc.tension * 100)}%</span>
            </div>
            {#if arc.stakes}
              <p class="journal-overlay__arc-stakes">
                Stakes: {arc.stakes}
              </p>
            {/if}
          </div>
        {/each}
      {/if}
    {:else if activeTab === "facts"}
      <!-- Key Facts: deep memory -->
      {#if memory && memory.deep_text}
        <div class="journal-overlay__section-label">
          KEY FACTS ({memory.deep_count})
        </div>
        {#each memory.deep_text.trim().split("\n") as line}
          {#if line.trim()}
            <p class="journal-overlay__memory-line">{line}</p>
          {/if}
        {/each}
      {:else}
        <span class="journal-overlay__empty">No key facts established yet.</span>
      {/if}
    {/if}
  </div>

  <div class="journal-overlay__border">+{"=".repeat(44)}+</div>
</div>

<style>
  .journal-overlay {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    max-height: 70vh;
  }

  .journal-overlay__title {
    color: var(--fg-bright);
    font-size: 1.1rem;
    letter-spacing: 0.15em;
  }

  .journal-overlay__border {
    color: var(--fg-dim);
    font-size: 0.85rem;
  }

  .journal-overlay__tabs {
    display: flex;
    gap: 0.25rem;
    width: 100%;
    max-width: 50ch;
  }

  .journal-overlay__tab {
    flex: 1;
    background: none;
    border: 1px solid var(--fg-dim);
    color: var(--fg-dim);
    font-family: inherit;
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    letter-spacing: 0.1em;
  }

  .journal-overlay__tab.active {
    color: var(--fg-bright);
    border-color: var(--fg-bright);
    background: rgba(255, 255, 255, 0.05);
  }

  .journal-overlay__content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    overflow-y: auto;
    width: 100%;
    max-width: 50ch;
    min-height: 10rem;
  }

  .journal-overlay__section-label {
    color: var(--fg-bright);
    font-size: 0.7rem;
    letter-spacing: 0.15em;
    border-bottom: 1px solid var(--fg-dim);
    padding-bottom: 0.15rem;
    margin-top: 0.5rem;
  }

  .journal-overlay__memory-line {
    color: var(--fg);
    font-size: 0.8rem;
    line-height: 1.3;
    padding-left: 0.5rem;
  }

  .journal-overlay__memory-line--dim {
    color: var(--fg-dim);
  }

  .journal-overlay__entry {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .journal-overlay__header {
    display: flex;
    gap: 0.5rem;
    align-items: baseline;
  }

  .journal-overlay__icon {
    color: var(--fg-bright);
    font-weight: bold;
  }

  .journal-overlay__entry-title {
    color: var(--fg);
    font-weight: bold;
  }

  .journal-overlay__type {
    color: var(--fg-dim);
    font-size: 0.7rem;
    margin-left: auto;
  }

  .journal-overlay__body {
    color: var(--fg-dim);
    font-size: 0.85rem;
    padding-left: 2rem;
    line-height: 1.4;
  }

  .journal-overlay__empty {
    color: var(--fg-dim);
    font-size: 0.85rem;
    text-align: center;
    padding: 1rem;
  }

  /* Story arc styles */
  .journal-overlay__arc {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  .journal-overlay__arc-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }

  .journal-overlay__arc-title {
    color: var(--fg-bright);
    font-weight: bold;
    font-size: 0.9rem;
  }

  .journal-overlay__arc-status {
    color: var(--fg-dim);
    font-size: 0.65rem;
    letter-spacing: 0.1em;
  }

  .journal-overlay__arc-status.climax {
    color: #ff6b6b;
  }

  .journal-overlay__arc-status.resolved {
    color: #51cf66;
  }

  .journal-overlay__arc-status.failed {
    color: #868e96;
  }

  .journal-overlay__arc-desc {
    color: var(--fg);
    font-size: 0.8rem;
    line-height: 1.3;
  }

  .journal-overlay__arc-tension {
    display: flex;
    gap: 0.5rem;
    align-items: baseline;
    font-size: 0.75rem;
  }

  .journal-overlay__arc-tension-label {
    color: var(--fg-dim);
    font-size: 0.65rem;
    letter-spacing: 0.1em;
  }

  .journal-overlay__arc-tension-bar {
    color: var(--fg);
    font-family: inherit;
  }

  .journal-overlay__arc-tension-pct {
    color: var(--fg-dim);
    font-size: 0.7rem;
  }

  .journal-overlay__arc-stakes {
    color: var(--fg-dim);
    font-size: 0.75rem;
    font-style: italic;
  }
</style>
